/**
 * Shared sector-string → canonical sector / archetype resolution.
 * Never throws.
 */

import {
    ARCHETYPE_LABELS,
    BENCHMARK_SOURCE_HEURISTIC,
    CANONICAL_SECTORS,
    FALLBACK_SECTOR_NOTE,
    HEURISTIC_EFFECTIVE_FROM,
    HISTORICAL_WINDOW_BY_ARCHETYPE,
    METHODOLOGY_NOTE,
    NOT_APPLICABLE_REASONS,
    SECTOR_ARCHETYPE_BY_ID,
} from '../config/sectorTaxonomy';

const ALIAS_INDEX = buildAliasIndex();

function buildAliasIndex() {
    const exact = new Map();
    const ordered = [];
    let order = 0;
    Object.entries(CANONICAL_SECTORS).forEach(([sectorId, def]) => {
        (def.aliases || []).forEach((rawAlias) => {
            const alias = normalizeSectorText(rawAlias);
            if (!alias) return;
            const entry = {
                alias,
                sectorId,
                order: order++,
                tokens: alias.split(' ').filter(Boolean),
            };
            ordered.push(entry);
            if (!exact.has(alias)) exact.set(alias, []);
            exact.get(alias).push(entry);
        });
    });
    return { exact, ordered };
}

function normalizeSectorText(raw) {
    if (raw == null) return '';
    return String(raw)
        .trim()
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function pickBestAliasMatch(matches) {
    if (!matches.length) return null;
    return matches.slice().sort((a, b) => {
        if (b.alias.length !== a.alias.length) return b.alias.length - a.alias.length;
        return a.order - b.order;
    })[0];
}

function consecutiveTokenMatch(sectorTokens, aliasTokens) {
    if (!aliasTokens.length || aliasTokens.length > sectorTokens.length) return false;
    for (let i = 0; i <= sectorTokens.length - aliasTokens.length; i += 1) {
        let matched = true;
        for (let j = 0; j < aliasTokens.length; j += 1) {
            if (sectorTokens[i + j] !== aliasTokens[j]) {
                matched = false;
                break;
            }
        }
        if (matched) return true;
    }
    return false;
}

function fallbackSector(rawSector) {
    return {
        sectorId: 'otherOperatingBusinesses',
        label: CANONICAL_SECTORS.otherOperatingBusinesses.label,
        rawSector: rawSector == null ? rawSector : String(rawSector),
        unmapped: true,
        archetypeId: SECTOR_ARCHETYPE_BY_ID.otherOperatingBusinesses,
    };
}

function sectorResult(sectorId, rawSector, unmapped) {
    const def = CANONICAL_SECTORS[sectorId] || CANONICAL_SECTORS.otherOperatingBusinesses;
    return {
        sectorId,
        label: def.label,
        rawSector: rawSector == null ? rawSector : String(rawSector),
        unmapped,
        archetypeId: SECTOR_ARCHETYPE_BY_ID[sectorId] || 'manufacturingIndustrial',
    };
}

/**
 * Map a source sector string onto a canonical Finlytics sector.
 * Never throws.
 *
 * @param {unknown} rawSector
 * @returns {{ sectorId: string, label: string, rawSector: unknown, unmapped: boolean, archetypeId: string }}
 */
export function normalizeSector(rawSector) {
    try {
        const normalized = normalizeSectorText(rawSector);
        if (!normalized) return fallbackSector(rawSector);

        const exactHits = ALIAS_INDEX.exact.get(normalized) || [];
        if (exactHits.length) {
            const best = pickBestAliasMatch(exactHits);
            return sectorResult(best.sectorId, rawSector, false);
        }

        const tokens = normalized.split(' ').filter(Boolean);
        const partialHits = ALIAS_INDEX.ordered.filter((entry) => consecutiveTokenMatch(tokens, entry.tokens));
        if (partialHits.length) {
            const best = pickBestAliasMatch(partialHits);
            return sectorResult(best.sectorId, rawSector, false);
        }

        return fallbackSector(rawSector);
    } catch (_err) {
        return fallbackSector(rawSector);
    }
}

export function isNotApplicableSector(sector) {
    if (!sector) return false;
    return sector.sectorId === 'financialInstitutions'
        || sector.sectorId === 'reitInvit'
        || sector.archetypeId === 'notApplicable';
}

/**
 * Shared sector profile used by statement-specific resolvers.
 *
 * @param {unknown} rawSector
 * @param {Record<string, object|null>} metricsByArchetype
 * @param {{ methodologyNote?: string, notApplicableReasons?: Record<string, string> }} [options]
 */
export function buildSectorBenchmarkProfile(rawSector, metricsByArchetype = {}, options = {}) {
    const methodologyNote = options.methodologyNote || METHODOLOGY_NOTE;
    const notApplicableReasons = options.notApplicableReasons || NOT_APPLICABLE_REASONS;
    try {
        const sector = normalizeSector(rawSector);
        const archetypeId = sector.archetypeId;
        const requiredWindow = HISTORICAL_WINDOW_BY_ARCHETYPE[archetypeId] || 3;
        const applicable = !isNotApplicableSector(sector);
        return {
            sector,
            archetypeId,
            archetypeLabel: ARCHETYPE_LABELS[archetypeId] || archetypeId,
            applicable,
            notApplicableReason: applicable ? null : (notApplicableReasons[sector.sectorId] || null),
            requiredWindow,
            primaryWindowLabel: requiredWindow === 5 ? '5-year median' : '3-year median',
            methodologyNote,
            source: BENCHMARK_SOURCE_HEURISTIC,
            effectiveFrom: HEURISTIC_EFFECTIVE_FROM,
            sampleSize: null,
            metrics: metricsByArchetype[archetypeId] || null,
            unmappedFallbackNote: sector.unmapped ? FALLBACK_SECTOR_NOTE : null,
        };
    } catch (_err) {
        const sector = fallbackSector(rawSector);
        return {
            sector,
            archetypeId: sector.archetypeId,
            archetypeLabel: ARCHETYPE_LABELS[sector.archetypeId],
            applicable: true,
            notApplicableReason: null,
            requiredWindow: 3,
            primaryWindowLabel: '3-year median',
            methodologyNote,
            source: BENCHMARK_SOURCE_HEURISTIC,
            effectiveFrom: HEURISTIC_EFFECTIVE_FROM,
            sampleSize: null,
            metrics: metricsByArchetype.manufacturingIndustrial || null,
            unmappedFallbackNote: FALLBACK_SECTOR_NOTE,
        };
    }
}
